import { useEffect, useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Plus, FileText, Play, Pencil, Trash2, Calendar, Clock, Search, CheckCircle, MoreHorizontal, ChevronLeft, Filter, ArrowUpDown, LayoutGrid, List } from "lucide-react"
import { playbookService } from "@/services/playbooks"
import { PlaybookTemplate } from "@/types/playbooks"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/components/ui/use-toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import DashboardLayout from "@/components/layout/DashboardLayout"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"

export default function Playbooks() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<PlaybookTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null)
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const [currentView, setCurrentView] = useState<"grid" | "list">("grid")
  const [sortBy, setSortBy] = useState<string>("created_at_desc")
  const [sidebarVisible, setSidebarVisible] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string>("all")
  
  useEffect(() => {
    loadTemplates()
  }, [])
  
  const loadTemplates = async () => {
    try {
      setIsLoading(true)
      const data = await playbookService.templates.getBySubscription()
      setTemplates(data)
    } catch (error) {
      console.error('Erro ao carregar templates:', error)
      toast({
        title: 'Erro ao carregar templates',
        variant: 'destructive',
        description: 'Não foi possível carregar os templates. Tente novamente.',
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleDelete = async () => {
    if (!deleteTemplateId) return
    
    try {
      await playbookService.templates.delete(deleteTemplateId)
      toast({
        title: 'Template excluído',
        description: 'O template foi excluído com sucesso.',
      })
      setDeleteTemplateId(null)
      loadTemplates()
    } catch (error) {
      console.error('Erro ao excluir template:', error)
      toast({
        title: 'Erro ao excluir template',
        variant: 'destructive',
        description: 'Não foi possível excluir o template. Tente novamente.',
      })
    }
  }
  
  const createNewRun = (templateId: string) => {
    navigate(`/playbooks/runs/new?templateId=${templateId}`)
  }

  // Filtra e ordena os templates baseado nos filtros e ordenação atual
  const filteredTemplates = templates
    .filter(template => {
      // Filtro por busca
      const matchesSearch = !searchQuery || 
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()))
      
      // Filtro por categoria
      const matchesCategory = selectedCategory === "all" ||
        (template.category === selectedCategory)
      
      return matchesSearch && matchesCategory
    })
    .sort((a, b) => {
      // Ordenação
      switch (sortBy) {
        case "name_asc":
          return a.name.localeCompare(b.name)
        case "name_desc":
          return b.name.localeCompare(a.name)
        case "created_at_asc":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        case "created_at_desc":
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })

  // Agrupar templates por categoria para o sidebar
  const categories = templates.reduce((acc, template) => {
    if (template.category && !acc.includes(template.category)) {
      acc.push(template.category)
    }
    return acc
  }, [] as string[])
  
  return (
    <DashboardLayout>
      <div className="px-4 py-6 w-full">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <div>
            <h1 className="text-3xl font-extrabold">Playbooks</h1>
            <p className="text-muted-foreground">
              Gerencie seus templates e execuções de playbooks
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-1" 
              onClick={() => setSidebarVisible(!sidebarVisible)}
            >
              <ChevronLeft className={`h-4 w-4 transition-transform ${sidebarVisible ? "" : "rotate-180"}`} />
              <span className="hidden sm:inline">{sidebarVisible ? "Ocultar painel" : "Mostrar painel"}</span>
            </Button>
            
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => navigate("/customer-success/playbooks/runs/new")}
            >
              <Play className="h-4 w-4" />
            </Button>
            
            <Button onClick={() => navigate("/customer-success/playbooks/templates/new")}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Template
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Sidebar */}
          <div 
            className={`col-span-12 md:col-span-3 lg:col-span-3 transition-all duration-300 ease-in-out
              ${sidebarVisible ? 'opacity-100 max-h-[2000px]' : 'opacity-0 max-h-0 overflow-hidden md:hidden'}
            `}
          >
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <div className="space-y-1">
                  <div className="font-extrabold uppercase text-xs text-muted-foreground mb-2">
                    CATEGORIAS
                  </div>
                  
                  <Button 
                    variant={selectedCategory === "all" ? "default" : "ghost"} 
                    className="w-full justify-start h-10"
                    onClick={() => setSelectedCategory("all")}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    <span className="text-sm">Todos os templates</span>
                    <Badge className="ml-auto" variant="secondary">{templates.length}</Badge>
                  </Button>
                  
                  {categories.map(category => (
                    <Button 
                      key={category}
                      variant={selectedCategory === category ? "default" : "ghost"} 
                      className="w-full justify-start h-10"
                      onClick={() => setSelectedCategory(category)}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      <span className="text-sm">{category}</span>
                      <Badge className="ml-auto" variant="secondary">
                        {templates.filter(t => t.category === category).length}
                      </Badge>
                    </Button>
                  ))}
                  
                  {categories.length === 0 && (
                    <div className="text-sm text-muted-foreground px-2 py-4">
                      Nenhuma categoria disponível
                    </div>
                  )}
                </div>
                
                <Separator className="my-4" />
                
                <div className="space-y-1">
                  <div className="font-extrabold uppercase text-xs text-muted-foreground mb-2">
                    EXECUÇÕES RECENTES
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="w-full justify-start h-10"
                    onClick={() => navigate("/customer-success/playbooks/runs")}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    <span className="text-sm">Ver todas as execuções</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Main Content */}
          <div className={`col-span-12 ${sidebarVisible ? 'md:col-span-9 lg:col-span-9' : 'md:col-span-12 lg:col-span-12'}`}>
            <Card className="shadow-sm">
              <CardHeader className="pb-3 px-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <CardTitle className="text-xl">
                      {selectedCategory === "all" ? "Todos os templates" : selectedCategory}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} disponíve{filteredTemplates.length !== 1 ? 'is' : 'l'}
                    </CardDescription>
                  </div>
                  
                  <div className="flex items-center">
                    <Tabs
                      value={currentView}
                      onValueChange={(v) => setCurrentView(v as "grid" | "list")}
                      className="w-full"
                    >
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="list" className="flex items-center gap-1">
                          <List className="h-4 w-4" />
                          <span className="sr-only sm:not-sr-only">Lista</span>
                        </TabsTrigger>
                        <TabsTrigger value="grid" className="flex items-center gap-1">
                          <LayoutGrid className="h-4 w-4" />
                          <span className="sr-only sm:not-sr-only">Grade</span>
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-4">
                {/* Barra de ferramentas */}
                <div className="flex flex-wrap items-center gap-2 pb-4">
                  <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Procurar templates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at_desc">Mais recentes</SelectItem>
                      <SelectItem value="created_at_asc">Mais antigos</SelectItem>
                      <SelectItem value="name_asc">Nome (A-Z)</SelectItem>
                      <SelectItem value="name_desc">Nome (Z-A)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {isLoading ? (
                  <div className={currentView === "grid" 
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" 
                    : "space-y-2"
                  }>
                    {[...Array(6)].map((_, i) => (
                      currentView === "grid" ? (
                        <Card key={i} className="min-h-[220px]">
                          <CardHeader className="pb-2">
                            <Skeleton className="h-5 w-3/4 mb-2" />
                            <Skeleton className="h-4 w-1/2" />
                          </CardHeader>
                          <CardContent>
                            <Skeleton className="h-4 w-full mb-2" />
                            <Skeleton className="h-4 w-3/4 mb-2" />
                          </CardContent>
                          <CardFooter>
                            <Skeleton className="h-8 w-full" />
                          </CardFooter>
                        </Card>
                      ) : (
                        <div key={i} className="flex items-center p-3 border rounded-md">
                          <div className="flex-1">
                            <Skeleton className="h-5 w-2/4 mb-2" />
                            <Skeleton className="h-4 w-1/4" />
                          </div>
                          <Skeleton className="h-8 w-24" />
                        </div>
                      )
                    ))}
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <h3 className="text-lg font-extrabold mb-1">Nenhum template encontrado</h3>
                    <p className="text-muted-foreground mb-4">
                      {searchQuery 
                        ? "Tente ajustar os termos da sua pesquisa" 
                        : "Crie seu primeiro template para começar"
                      }
                    </p>
                    <Button 
                      onClick={() => navigate('/customer-success/playbooks/templates/new')}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Criar Novo Template
                    </Button>
                  </div>
                ) : currentView === "grid" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredTemplates.map((template) => (
                      <Card key={template.id} className="flex flex-col hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2">
                          <CardTitle className="line-clamp-1 text-lg">{template.name}</CardTitle>
                          <CardDescription className="flex items-center gap-1 text-xs">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(template.created_at), "dd 'de' MMMM, yyyy", { locale: ptBR })}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="flex-1 pt-0">
                          <p className="line-clamp-2 text-sm text-muted-foreground mb-2">
                            {template.description || "Sem descrição"}
                          </p>
                          {template.category && (
                            <Badge variant="outline" className="mt-1">{template.category}</Badge>
                          )}
                        </CardContent>
                        <CardFooter className="flex flex-col gap-2 pt-0">
                          <div className="flex items-center w-full gap-2">
                            <Button 
                              variant="default" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => createNewRun(template.id)}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Iniciar
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/customer-success/playbooks/templates/edit/${template.id}`)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteTemplateId(template.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardFooter>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredTemplates.map((template) => (
                      <div 
                        key={template.id} 
                        className="flex items-center justify-between p-2 border-b hover:bg-gray-50/50 transition-colors"
                      >
                        <div className="flex-1 min-w-0 mr-4">
                          <h3 className="font-extrabold truncate">{template.name}</h3>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(template.created_at), "dd/MM/yyyy", { locale: ptBR })}
                            </span>
                            {template.category && (
                              <Badge variant="outline" className="h-5 text-xs">{template.category}</Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => createNewRun(template.id)}
                          >
                            <Play className="h-4 w-4" />
                            <span className="sr-only md:not-sr-only md:ml-1">Iniciar</span>
                          </Button>
                          
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/customer-success/playbooks/templates/edit/${template.id}`)}
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only md:not-sr-only md:ml-1">Editar</span>
                          </Button>
                          
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteTemplateId(template.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Excluir</span>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        <AlertDialog open={!!deleteTemplateId} onOpenChange={(open) => !open && setDeleteTemplateId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir template</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir este template? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive hover:bg-destructive/90" onClick={handleDelete}>
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  )
}